<template lang="pug">
BriqPanel.general-settings-panel
  BriqMultilangueColumns(
    :showSecondColumn="showSecondColumn"
    )
    h2.mb-S General settings
    BriqInput(label="Key" name="Key" v-model="product.Key" disabled :hideOptionalMarker="true")

    label.input-label Availability
    BriqCheckbox.m-0(label="Active" name="Enabled" v-model="product.Enabled" :disabled="disabled")
    BriqCheckbox.m-0(label="Available online" name="AvailableOnline" v-model="product.AvailableOnline" :disabled="disabled")
    BriqCheckbox.m-0(label="Show in e-mail" name="ShowInProgram" v-model="product.ShowInProgram" :disabled="disabled")
    BriqCheckbox.m-0(
      label="Exclusive" 
      name="AlwaysExclusive" 
      v-model="product.AlwaysExclusive" 
      :disabled="disabled || isResourceExclusive || isResourceHasTeamSize" 
      data-test-id="always-exclusive"
      )

    hr

    BriqInput(
      label="Name"
      placeholder="Name"
      name="NameInternal"
      data-test-id="name-internal"
      v-model="product.NameInternal"
      required
      :disabled="disabled"
      :error="nameInternalError"
      @blur="populatePublicNameAndTouchVuelidate()"
      )
    BriqInput(
      label="Position"
      placeholder="Position"
      name="Position"
      v-model="product.Position"
      type="number"
      step="0.1"
      help="Can be used to sort products in the admin selection dropdowns. The product with the lowest number appears on top."
      :disabled="disabled"
      )
    div(v-if="availableSegments && !availableSegments.SegmentlessResource")
      BriqToggleSwitch(
        v-model="customSegments"
        label="Customize Customer Segments"
        data-test-id="segment-toggle"
        )
      div(v-if="customSegments")
        label.input-label Available for segment
        BriqCheckbox.m-0(
          v-for="s in availableSegments.Segments"
          :key="s.SegmentCode"
          :data-test-id="checkboxTestId(s.SegmentCode)"
          :label="s.SegmentName"
          :disabled="disabled"
          :value="segmentIsActive(s)"
          @input="toggleSegment(s)"
          )
</template>

<script>
import BriqDurationInput from '@v/components/BriqDurationInput.vue'
import BriqIsoDurationInput from '@v/components/BriqIsoDurationInput.vue'
import { getHumanReadableTime } from './humanReadableTimeHelper.js'
import BriqMultilangueColumns from '@v/components/BriqMultilanguageColumns.vue'
import modelMixin from '@v/mixins/modelMixin'
import {
  pipe,
  equals,
  filter,
  lensProp,
  over,
  not,
  append,
  includes,
  forEach,
  has,
  keys,
  length,
  prop,
  toLower,
  flip,
  mapObjIndexed,
  map,
  clone,
  eqProps,
} from 'ramda'

export default {
  name: 'GeneralSettingsPanel',
  components: {
    BriqDurationInput,
    BriqMultilangueColumns,
    BriqIsoDurationInput,
  },
  mixins: [modelMixin({ type: Object, name: 'product' })],
  props: {
    vuelidate: Object,
    disabled: Boolean,
    resourceOptions: Array,
    showSecondColumn: Boolean,
    availableSegments: Object,
    enableProductDurationPerParticipant: Boolean,
  },
  data() {
    return {
      durationSuggestedValue: '',
      resetTimeSuggestedValue: '',
      innerCustomSegments: false,
      previousActiveSegments: null,
      hideResetAndApplicableTime: false,
    }
  },
  computed: {
    nameInternalError() {
      if (!this.vuelidate.product.NameInternal.$error) return null
      return 'Please enter a name'
    },
    selectedResource() {
      return this.resourceOptions.find(resource => {
        return resource.Key === this.value.ResourceKey
      })
    },
    customSegments: {
      get() {
        return this.innerCustomSegments
      },
      set(val) {
        this.innerCustomSegments = val
        const anyActiveSegments = length(this.product.ActiveSegments)
        if (val && !anyActiveSegments) {
          if (this.previousActiveSegments) {
            this.product.ActiveSegments = this.previousActiveSegments
          } else {
            this.enableAllAvailableSegments(this.availableSegments)
          }
        } else if (!val && anyActiveSegments) {
          this.previousActiveSegments = clone(this.product.ActiveSegments)
          this.product.ActiveSegments = null
        }
      },
    },
    isResourceExclusive() {
      return this.selectedResource?.Exclusive
    },
    isResourceHasTeamSize() {
      return this.selectedResource?.TeamSize > 0
    },
    productHasActiveSegments() {
      return length(this.product.ActiveSegments) > 0
    },
  },
  methods: {
    toggleSegment(segment) {
      if (this.segmentIsActive(segment)) {
        this.product.ActiveSegments = filter(s => s.SegmentCode !== segment.SegmentCode)(
          this.product.ActiveSegments,
        )
      } else {
        this.product.ActiveSegments.push(segment)
      }
    },
    segmentIsActive(segment) {
      const segmentExists = eqProps('SegmentCode', segment)
      return length(filter(segmentExists)(this.product.ActiveSegments)) > 0
    },
    checkboxTestId(segmentCode) {
      return `segment-checkbox-${segmentCode.toLowerCase()}`
    },
    populatePublicNameAndTouchVuelidate() {
      this.product.NameExternal =
        this.product.NameExternal === null || this.product.NameExternal === ''
          ? this.product.NameInternal
          : this.product.NameExternal

      this.vuelidate.product.NameInternal.$touch()
    },
    enableAllAvailableSegments(availableSegments) {
      if (length(availableSegments.Segments) > 0) {
        this.product.ActiveSegments = []
        forEach(seg => {
          this.product.ActiveSegments.push(seg)
        })(availableSegments.Segments)
      } else this.product.ActiveSegments = null
    },
    filterUnavailableSegments(availableSegments) {
      // filter out segments that are no longer available (because user changed the resource)
      const segmentExistsInAvailableSegmentsList = seg =>
        availableSegments.findIndex(value => value.SegmentCode === seg.SegmentCode) !== -1

      this.product.ActiveSegments = filter(
        segmentExistsInAvailableSegmentsList,
        this.ActiveSegments,
      )
    },
  },
  watch: {
    availableSegments(newVal) {
      if (this.innerCustomSegments) {
        if (!this.product.Key) this.enableAllAvailableSegments(newVal)
        else this.filterUnavailableSegments(newVal)
      }
    },
    'selectedResource.Exclusive': function(newVal) {
      this.product.AlwaysExclusive = newVal ? true : false
    },
    'product.ActiveSegments': function() {
      this.innerCustomSegments = this.productHasActiveSegments
    },
  },
  mounted() {
    this.innerCustomSegments = this.productHasActiveSegments
    if (!this.product.AlwaysExclusive)
      this.product.AlwaysExclusive = this.selectedResource?.Exclusive || false
  },
}
</script>

<style lang="stylus" scoped>
@import '~@v/variables.styl'

.input-label
  {typeSmall}
  display inline-block
  max-width 100%
  margin-bottom 5px
</style>

<style lang="stylus">
@import '~@v/variables.styl'

.general-settings-panel .briq-form-element
  margin-bottom space-S

.general-settings-panel .briq-form-element.m-0
  margin-bottom 0
</style>
